import { useEffect, useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetJobDraftQuery, useGetJobDraftsQuery } from "@js/apps/jobs/api";
import { useGetRolesQuery } from "@js/apps/roles/api";
import { ModalInstance } from "@js/components/modal";
import { useAppDispatch } from "@js/hooks";

import { setCurrentPostedATSJobId } from "../../components/ats-integration-modal/ats-jobs-slice";

export const useDraftJobToReview = () => {
  const dispatch = useAppDispatch();
  const {
    data: drafts,
    isFetching: isLoadingDrafts,
    refetch: refetchDrafts,
  } = useGetJobDraftsQuery({});
  void useGetRolesQuery();
  const draftsToReview = useMemo(
    () =>
      drafts?.results
        .filter((draft) => !!draft.ats_imported)
        .map((d) => d.id) || [],
    [drafts?.results],
  );
  const [currentReviewedDraftId, setCurrentReviewedDraftId] =
    useState<number>();

  const { currentData: draft, isFetching: isLoadingDraft } =
    useGetJobDraftQuery(
      currentReviewedDraftId ? { id: currentReviewedDraftId } : skipToken,
    );

  const indexOfCurrentReview =
    typeof currentReviewedDraftId !== "undefined"
      ? draftsToReview?.indexOf(currentReviewedDraftId)
      : -1;

  const handleSkipClick = () => {
    if (indexOfCurrentReview < draftsToReview.length - 1) {
      setCurrentReviewedDraftId(draftsToReview[indexOfCurrentReview + 1]);
      return;
    }
    ModalInstance.close();
  };

  const handleBackClick = () => {
    if (indexOfCurrentReview > 0) {
      setCurrentReviewedDraftId(draftsToReview[indexOfCurrentReview - 1]);
      return;
    }
    ModalInstance.close();
  };

  useEffect(() => {
    if (isLoadingDrafts || !draftsToReview.length) {
      return;
    }
    const firstDraftToReview = draftsToReview[0];
    setCurrentReviewedDraftId((prev) => {
      if (prev && draftsToReview.includes(prev)) {
        return prev;
      }

      return firstDraftToReview;
    });
  }, [draftsToReview, isLoadingDrafts]);

  useEffect(() => {
    return () => {
      dispatch(setCurrentPostedATSJobId(undefined));
    };
  }, [dispatch]);

  return {
    draftsCountToReview: draftsToReview.length,
    isLoadingDrafts: isLoadingDrafts,
    isLoadingDraft: isLoadingDraft || !draft,
    currentReviewedDraftId,
    draft,
    currentDraftCount: indexOfCurrentReview + 1,
    handleSkipClick,
    handleBackClick,
    refetchDrafts,
  };
};
